export interface HttpOptions extends RequestInit { json?: any; auth?: boolean; }

async function getTokens(): Promise<{ apiToken: string | null; graphToken: string | null }> {
    try {
        const mod = await import('../auth/msal');
        if (typeof mod.getApiAndGraphTokens === 'function') {
            const tokens = await mod.getApiAndGraphTokens();
            // basic debug logging to verify Graph/API token acquisition path
            console.debug('getTokens: using getApiAndGraphTokens, apiToken present:', !!tokens.apiToken, 'graphToken present:', !!tokens.graphToken);
            return tokens;
        }
        // Fallback: only API token via getAccessTokenSilent
        const apiToken = await mod.getAccessTokenSilent();
        console.debug('getTokens: fallback to getAccessTokenSilent, apiToken present:', !!apiToken);
        return { apiToken, graphToken: null };
    } catch {
        console.warn('getTokens: failed to load msal or acquire token');
        return { apiToken: null, graphToken: null };
    }
}

async function triggerReauth() {
    try {
        const mod = await import('../auth/msal');
        await mod.ensureReauth(false);
    } catch { /* ignore */ }
}

export async function http<T = any>(url: string, options: HttpOptions = {}): Promise<T> {
    const { json, headers, auth = true, ...rest } = options;
    const init: RequestInit = {
        ...rest,
        headers: { 'Content-Type': 'application/json', ...(headers || {}) }
    };
    if (json !== undefined) init.body = JSON.stringify(json);

    if (auth) {
        const { apiToken, graphToken } = await getTokens();
        if (!apiToken) {
            // Attempt to re-authenticate and do NOT send the request without a token
            await triggerReauth();
            throw new Error('401 Unauthorized');
        }
        (init.headers as any)['Authorization'] = `Bearer ${apiToken}`;
        if (graphToken) {
            // Graph token gaat mee voor backend-Graph-calls
            (init.headers as any)['X-Graph-Access-Token'] = graphToken;
        }
    }

    const res = await fetch(url, init);

    if (auth && res.status === 401) {
        await triggerReauth();
        throw new Error('401 Unauthorized');
    }

    if (res.status === 403) throw new Error('Not authorized. Ask an administrator to add your email address.');
    const ct = res.headers.get('content-type') || '';

    // Read body exactly once
    const raw = await res.text();
    let parsed: any = null;
    const looksJson = ct.includes('application/json');

    if (looksJson && raw) {
        try {
            parsed = JSON.parse(raw);
        } catch (err) {
            // Debug help: you can temporarily log raw + error if needed
            console.warn('http(): failed to parse JSON response', err, raw);
        }
    }

    if (!res.ok) {
        // Prefer JSON payload if available
        if (parsed && typeof parsed === 'object') {
            const serverMessage = parsed.message || parsed.error || `${res.status} ${res.statusText}`;
            const err: any = new Error(serverMessage);
            err.payload = parsed;
            err.status = res.status;
            throw err;
        }

        // Fallback: use raw text
        const msg = raw
            ? `${res.status} ${res.statusText}: ${raw}`
            : `${res.status} ${res.statusText}`;
        throw new Error(msg);
    }

    // Success path
    if (parsed && typeof parsed === 'object') {
        return parsed as T;
    }

    // Non-JSON success, return raw text
    return raw as any as T;
}